import { useCallback, useEffect, useRef, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { useSearchParams } from "react-router-dom";
import { adminService } from "../../features/admin/service";
import { formatIST } from "../../utils/dateUtils";
import Pagination from "../../components/admin/Pagination";
import { useAdminToast } from "../../context/AdminToastContext";

// "reason" is which gate flagged this deal — drives both the badge shown per
// row and which backend endpoint (last-minute-deals vs cancelled-deals) an
// approve/reject action is sent to. Set from which list a deal was fetched
// from, not from parsing admin_review.flagged_reason client-side, since the
// gate only ever sets ONE reason per deal today (see typed/service.py's
// sequential/short-circuit gate) — the source list IS the reason.
type FlagReason = "last_minute" | "cancelled";

interface FlaggedDeal {
  id: string;
  collection: string;
  title: string;
  dealer?: { id?: string; name?: string };
  categories?: { primary_slug?: string; secondary_slugs?: string[]; subcategory_slugs?: string[] };
  tags?: string[];
  admin_review?: {
    status?: string;
    flagged_reason?: string;
    reviewed_by?: string | null;
    reviewed_at?: string | null;
    rejection_reason?: string | null;
  };
  reviewed_by_name?: string;
  is_active?: boolean;
  updated_at?: string;
  reason: FlagReason;
}

const REASON_LABEL: Record<FlagReason, string> = {
  last_minute: "Last-Minute",
  cancelled: "Cancelled",
};

const REASON_BADGE_CLASS: Record<FlagReason, string> = {
  last_minute: "bg-amber-500/10 text-amber-400",
  cancelled: "bg-red-500/10 text-red-400",
};

type Tab = "pending" | "reviewed";

export default function AdminLastMinuteReviewPage() {
  const [tab, setTab] = useState<Tab>("pending");
  const [pending, setPending] = useState<FlaggedDeal[]>([]);
  const [reviewed, setReviewed] = useState<FlaggedDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<FlaggedDeal | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approveTarget, setApproveTarget] = useState<FlaggedDeal | null>(null);
  // Separate paging state per tab (pending/reviewed are two independent
  // lists) — same shared <Pagination> component and 0-indexed `page` contract
  // used everywhere else in the admin panel (e.g. AdminDealsPage.tsx).
  const [pendingPage, setPendingPage] = useState(0);
  const [pendingPageSize, setPendingPageSize] = useState(10);
  const [reviewedPage, setReviewedPage] = useState(0);
  const [reviewedPageSize, setReviewedPageSize] = useState(10);
  // Deep-link target from an admin notification (?deal_id=... — see
  // AdminNotificationsCenter.tsx:goToUser). Highlighted + scrolled into view
  // once the matching list has loaded; jumps to the "reviewed" tab if the
  // deal's already been decided (e.g. stale/re-clicked notification).
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightRefs = useRef<Record<string, HTMLElement | null>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lmPending, lmReviewed, cdPending, cdReviewed] = await Promise.all([
        adminService.listPendingLastMinuteDeals(),
        adminService.listReviewedLastMinuteDeals(),
        adminService.listPendingCancelledDeals(),
        adminService.listReviewedCancelledDeals(),
      ]);
      const tag = (reason: FlagReason) => (items: any[]) => items.map((d) => ({ ...d, reason }));
      const byUpdatedAtDesc = (a: FlaggedDeal, b: FlaggedDeal) =>
        (b.updated_at || "").localeCompare(a.updated_at || "");
      setPending([...tag("last_minute")(lmPending), ...tag("cancelled")(cdPending)].sort(byUpdatedAtDesc));
      setReviewed([...tag("last_minute")(lmReviewed), ...tag("cancelled")(cdReviewed)].sort(byUpdatedAtDesc));
    } catch (err) {
      console.error("Failed to load flagged-deal review data:", err);
      setError("Failed to load flagged-deal review data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Clamp each tab's page back into range whenever its list shrinks (e.g. an
  // approve/reject moves an item out of "pending", or a reload returns fewer
  // rows) — otherwise a stale higher page number would render an empty list.
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(pending.length / pendingPageSize) - 1);
    setPendingPage((p) => Math.min(p, maxPage));
  }, [pending, pendingPageSize]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(reviewed.length / reviewedPageSize) - 1);
    setReviewedPage((p) => Math.min(p, maxPage));
  }, [reviewed, reviewedPageSize]);

  useEffect(() => {
    const dealId = searchParams.get("deal_id");
    if (!dealId || loading) return;
    const pendingIndex = pending.findIndex((d) => d.id === dealId);
    const reviewedIndex = reviewed.findIndex((d) => d.id === dealId);
    if (pendingIndex === -1 && reviewedIndex === -1) return;
    if (pendingIndex !== -1) {
      setTab("pending");
      setPendingPage(Math.floor(pendingIndex / pendingPageSize));
    } else {
      setTab("reviewed");
      setReviewedPage(Math.floor(reviewedIndex / reviewedPageSize));
    }
    setHighlightId(dealId);
    // Scrolling happens next render, once the now-correct page has painted
    // the highlighted row into the DOM.
    setTimeout(() => highlightRefs.current[dealId]?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
    const next = new URLSearchParams(searchParams);
    next.delete("deal_id");
    setSearchParams(next, { replace: true });
    const timer = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pending, reviewed]);

  const handleApprove = async (item: FlaggedDeal, publish: boolean) => {
    setBusyId(item.id);
    try {
      if (item.reason === "cancelled") {
        await adminService.approveCancelledDeal(item.collection, item.id, publish);
      } else {
        await adminService.approveLastMinuteDeal(item.collection, item.id, publish);
      }
      setApproveTarget(null);
      await load();
    } catch (err) {
      console.error("Failed to approve deal:", err);
      setError(`Failed to approve "${item.title}".`);
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    setBusyId(rejectTarget.id);
    try {
      if (rejectTarget.reason === "cancelled") {
        await adminService.rejectCancelledDeal(rejectTarget.collection, rejectTarget.id, rejectReason.trim());
      } else {
        await adminService.rejectLastMinuteDeal(rejectTarget.collection, rejectTarget.id, rejectReason.trim());
      }
      setRejectTarget(null);
      setRejectReason("");
      await load();
    } catch (err) {
      console.error("Failed to reject deal:", err);
      setError(`Failed to reject "${rejectTarget.title}".`);
    } finally {
      setBusyId(null);
    }
  };

  const pagedPending = pending.slice(pendingPage * pendingPageSize, (pendingPage + 1) * pendingPageSize);
  const pagedReviewed = reviewed.slice(reviewedPage * reviewedPageSize, (reviewedPage + 1) * reviewedPageSize);

  return (
    <div className="p-6">
      <AdminPageHeader
        className="mb-4"
        title="Flagged Deals Review"
        subtitle="Deals flagged as last-minute or cancelled (by category, subcategory, or tag) wait here until approved."
        actions={
          <button
            onClick={() => void load()}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
          >
            Refresh
          </button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-800">
        <button
          onClick={() => setTab("pending")}
          className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px ${
            tab === "pending" ? "border-emerald-500 text-white" : "border-transparent text-gray-500 hover:text-gray-300"
          }`}
        >
          Pending ({pending.length})
        </button>
        <button
          onClick={() => setTab("reviewed")}
          className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px ${
            tab === "reviewed" ? "border-emerald-500 text-white" : "border-transparent text-gray-500 hover:text-gray-300"
          }`}
        >
          Reviewed history ({reviewed.length})
        </button>
      </div>

      {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : tab === "pending" ? (
        pending.length === 0 ? (
          <p className="text-sm text-gray-400">No deals pending review.</p>
        ) : (
          <>
            <div className="space-y-3">
              {pagedPending.map((item) => (
                <div
                  key={`${item.collection}:${item.id}`}
                  ref={(el) => { highlightRefs.current[item.id] = el; }}
                  className={`rounded-lg border p-4 transition-colors ${
                    highlightId === item.id
                      ? "border-emerald-500 bg-emerald-500/10 ring-2 ring-emerald-500/40"
                      : "border-gray-700 bg-gray-900"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-white">{item.title}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${REASON_BADGE_CLASS[item.reason]}`}>
                          {REASON_LABEL[item.reason]}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Seller: {item.dealer?.name || "Unknown"} · Collection: {item.collection}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Category: {item.categories?.primary_slug || "—"}
                        {item.categories?.secondary_slugs?.length ? ` · Secondary: ${item.categories.secondary_slugs.join(", ")}` : ""}
                        {item.tags?.length ? ` · Tags: ${item.tags.join(", ")}` : ""}
                      </p>
                      {item.updated_at && (
                        <p className="text-[11px] text-gray-600 mt-1">Flagged {formatIST(item.updated_at)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setApproveTarget(item)}
                        disabled={busyId === item.id}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                      >
                        {busyId === item.id ? "Working…" : "Approve"}
                      </button>
                      <button
                        onClick={() => { setRejectTarget(item); setRejectReason(""); }}
                        disabled={busyId === item.id}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600/80 text-white hover:bg-red-600 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              page={pendingPage}
              pageSize={pendingPageSize}
              total={pending.length}
              onPageChange={setPendingPage}
              onPageSizeChange={setPendingPageSize}
              variant="panel"
            />
          </>
        )
      ) : reviewed.length === 0 ? (
        <p className="text-sm text-gray-400">No decisions recorded yet.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="w-full text-xs">
              <thead className="bg-gray-800 text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Deal</th>
                  <th className="px-3 py-2 text-left font-semibold">Reason</th>
                  <th className="px-3 py-2 text-left font-semibold">Seller</th>
                  <th className="px-3 py-2 text-left font-semibold">Decision</th>
                  <th className="px-3 py-2 text-left font-semibold">Live?</th>
                  <th className="px-3 py-2 text-left font-semibold">Reviewed by</th>
                  <th className="px-3 py-2 text-left font-semibold">When</th>
                  <th className="px-3 py-2 text-left font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800 bg-gray-900">
                {pagedReviewed.map((item) => (
                  <tr
                    key={`${item.collection}:${item.id}`}
                    ref={(el) => { highlightRefs.current[item.id] = el; }}
                    className={highlightId === item.id ? "bg-emerald-500/10 ring-2 ring-inset ring-emerald-500/40" : undefined}
                  >
                    <td className="px-3 py-2 text-white">{item.title}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${REASON_BADGE_CLASS[item.reason]}`}>
                        {REASON_LABEL[item.reason]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-400">{item.dealer?.name || "Unknown"}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          item.admin_review?.status === "approved"
                            ? "bg-emerald-500/10 text-emerald-400"
                            : "bg-red-500/10 text-red-400"
                        }`}
                      >
                        {item.admin_review?.status === "approved" ? "Approved" : "Rejected"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-400">{item.is_active ? "Yes" : "No"}</td>
                    <td className="px-3 py-2 text-gray-400">{item.reviewed_by_name || "—"}</td>
                    <td className="px-3 py-2 text-gray-500">
                      {item.admin_review?.reviewed_at ? formatIST(item.admin_review.reviewed_at) : "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{item.admin_review?.rejection_reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={reviewedPage}
            pageSize={reviewedPageSize}
            total={reviewed.length}
            onPageChange={setReviewedPage}
            onPageSizeChange={setReviewedPageSize}
            variant="panel"
          />
        </>
      )}

      {approveTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-5 max-w-sm w-full">
            <h3 className="text-sm font-bold text-white mb-1">Approve "{approveTarget.title}"</h3>
            <p className="text-xs text-gray-400 mb-4">
              Publish it live right now, or just clear the review gate and let the seller publish it themselves?
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => void handleApprove(approveTarget, true)}
                disabled={busyId === approveTarget.id}
                className="py-2 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                {busyId === approveTarget.id ? "Working…" : "Approve & publish now"}
              </button>
              <button
                onClick={() => void handleApprove(approveTarget, false)}
                disabled={busyId === approveTarget.id}
                className="py-2 text-xs font-semibold rounded-lg bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50"
              >
                {busyId === approveTarget.id ? "Working…" : "Approve only — seller publishes"}
              </button>
              <button
                onClick={() => setApproveTarget(null)}
                disabled={busyId === approveTarget.id}
                className="py-2 text-xs font-medium rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-5 max-w-sm w-full">
            <h3 className="text-sm font-bold text-white mb-1">Reject "{rejectTarget.title}"</h3>
            <p className="text-xs text-gray-400 mb-3">The seller will see this reason and can edit + resubmit.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection…"
              rows={3}
              className="w-full rounded-lg bg-gray-800 border border-gray-700 text-sm text-white p-2 mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setRejectTarget(null)}
                className="flex-1 py-2 text-xs font-medium rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleReject()}
                disabled={!rejectReason.trim() || busyId === rejectTarget.id}
                className="flex-1 py-2 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-500 disabled:opacity-50"
              >
                {busyId === rejectTarget.id ? "Rejecting…" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
